/**
 * SimulationPanel - Monte Carlo bracket simulation results.
 *
 * Premium-only feature that runs N simulations and shows each team's
 * probability of reaching each round. Includes strategy selector,
 * region filter, sortable table, and top contenders highlights.
 */

import { useState, useMemo } from 'react';
import { runSimulation, getSimulation } from '../../services/bracketService';

const ROUND_COLS = [
  { key: 'pct_round_of_32', label: 'R32', short: 'R32' },
  { key: 'pct_sweet_16', label: 'S16', short: 'S16' },
  { key: 'pct_elite_8', label: 'E8', short: 'E8' },
  { key: 'pct_final_4', label: 'F4', short: 'F4' },
  { key: 'pct_championship', label: 'Title Game', short: 'CG' },
  { key: 'pct_champion', label: 'Champ', short: 'W' },
];

const STRATEGIES = [
  { value: 'chalk', label: 'Chalk', desc: 'Favors higher seeds' },
  { value: 'balanced', label: 'Balanced', desc: 'Even mix of chalk and upsets' },
  { value: 'upset_hunter', label: 'Upset Hunter', desc: 'Aggressively picks upsets' },
];

const REGIONS = ['All', 'East', 'West', 'South', 'Midwest'];

export default function SimulationPanel({ season, onClose }) {
  const [strategy, setStrategy] = useState('balanced');
  const [simData, setSimData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState('pct_champion');
  const [sortDir, setSortDir] = useState('desc');
  const [regionFilter, setRegionFilter] = useState('All');
  const [simCount, setSimCount] = useState(10000);

  const handleRun = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await runSimulation(season, { strategy, num_simulations: simCount });
      setSimData(data);
      setSortCol('pct_champion');
      setSortDir('desc');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to run simulation');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadCached = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSimulation(season, strategy);
      setSimData(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No cached results. Run a new simulation.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load simulation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const filteredResults = useMemo(() => {
    if (!simData?.results) return [];
    let results = simData.results;
    if (regionFilter !== 'All') {
      results = results.filter(r => r.region === regionFilter);
    }
    return [...results].sort((a, b) => {
      const av = a[sortCol] ?? 0;
      const bv = b[sortCol] ?? 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [simData, regionFilter, sortCol, sortDir]);

  const topContenders = useMemo(() => {
    if (!simData?.results) return [];
    return [...simData.results]
      .sort((a, b) => b.pct_champion - a.pct_champion)
      .slice(0, 4);
  }, [simData]);

  return (
    <div className="glass-card p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-text-primary flex items-center gap-2">
            <svg className="w-5 h-5 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            Monte Carlo Simulation
          </h3>
          <p className="text-xs text-text-tertiary mt-0.5">
            Run thousands of bracket simulations to find each team's probability of advancing
          </p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary transition-colors p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] text-text-tertiary uppercase tracking-wider mb-1">Strategy</label>
          <div className="flex gap-1">
            {STRATEGIES.map(s => (
              <button
                key={s.value}
                onClick={() => setStrategy(s.value)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                  strategy === s.value
                    ? 'border-accent-blue bg-accent-blue/10 text-accent-blue font-semibold'
                    : 'border-border/50 text-text-secondary hover:border-border'
                }`}
                title={s.desc}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] text-text-tertiary uppercase tracking-wider mb-1">Simulations</label>
          <select
            value={simCount}
            onChange={(e) => setSimCount(parseInt(e.target.value))}
            className="input-glow text-xs py-1.5 px-2 bg-bg-card"
          >
            <option value={1000}>1,000</option>
            <option value={5000}>5,000</option>
            <option value={10000}>10,000</option>
            <option value={25000}>25,000</option>
            <option value={50000}>50,000</option>
          </select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleRun}
            disabled={loading}
            className="btn-gradient px-4 py-1.5 text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center gap-1.5"
          >
            {loading ? (
              <>
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Running...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                </svg>
                Run Simulation
              </>
            )}
          </button>
          <button
            onClick={handleLoadCached}
            disabled={loading}
            className="px-3 py-1.5 text-xs text-text-secondary border border-border/50 rounded-lg hover:border-border transition-all disabled:opacity-50"
          >
            Load Cached
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-danger/10 border border-danger/30 rounded-lg px-3 py-2 text-xs text-danger">
          {error}
        </div>
      )}

      {/* Results */}
      {simData && (
        <>
          {/* Top Contenders */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {topContenders.map((team, i) => (
              <div key={team.team_entry_id} className={`rounded-lg border p-3 text-center ${
                i === 0 ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-border/30 bg-bg-primary/30'
              }`}>
                <div className="text-[10px] text-text-tertiary uppercase tracking-wider">
                  {i === 0 ? 'Favorite' : `#${i + 1}`}
                </div>
                <div className="text-sm font-bold text-text-primary mt-1 truncate">
                  #{team.seed} {team.team_name}
                </div>
                <div className="text-xs text-text-tertiary">{team.region}</div>
                <div className={`text-lg font-black mt-1 ${i === 0 ? 'text-yellow-400' : 'text-accent-blue'}`}>
                  {team.pct_champion.toFixed(1)}%
                </div>
                <div className="text-[10px] text-text-tertiary">to win it all</div>
              </div>
            ))}
          </div>

          {/* Meta info */}
          <div className="flex items-center justify-between text-[10px] text-text-tertiary">
            <span>
              {simData.num_simulations.toLocaleString()} simulations | Strategy: {simData.strategy} | Run: {simData.simulation_run_id}
            </span>
            {simData.created_at && (
              <span>{new Date(simData.created_at).toLocaleString()}</span>
            )}
          </div>

          {/* Region filter */}
          <div className="flex gap-1">
            {REGIONS.map(r => (
              <button
                key={r}
                onClick={() => setRegionFilter(r)}
                className={`px-2 py-1 text-[10px] rounded transition-all ${
                  regionFilter === r
                    ? 'bg-accent-blue/15 text-accent-blue font-semibold'
                    : 'text-text-tertiary hover:text-text-secondary'
                }`}
              >
                {r}
              </button>
            ))}
          </div>

          {/* Results table */}
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/30">
                  <th className="text-left py-2 px-1 text-text-tertiary font-medium w-8">#</th>
                  <th className="text-left py-2 px-1 text-text-tertiary font-medium">Team</th>
                  <th className="text-center py-2 px-1 text-text-tertiary font-medium w-10">Seed</th>
                  {ROUND_COLS.map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className={`text-right py-2 px-1 font-medium cursor-pointer transition-colors w-14 ${
                        sortCol === col.key ? 'text-accent-blue' : 'text-text-tertiary hover:text-text-secondary'
                      }`}
                    >
                      <span className="flex items-center justify-end gap-0.5">
                        {col.short}
                        {sortCol === col.key && (
                          <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d={sortDir === 'desc' ? 'M19 9l-7 7-7-7' : 'M5 15l7-7 7 7'} />
                          </svg>
                        )}
                      </span>
                    </th>
                  ))}
                  <th
                    onClick={() => handleSort('avg_games_won')}
                    className={`text-right py-2 px-1 font-medium cursor-pointer transition-colors w-12 ${
                      sortCol === 'avg_games_won' ? 'text-accent-blue' : 'text-text-tertiary hover:text-text-secondary'
                    }`}
                  >
                    Avg W
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((team, idx) => (
                  <tr key={team.team_entry_id} className="border-b border-border/10 hover:bg-bg-primary/30 transition-colors">
                    <td className="py-1.5 px-1 text-text-tertiary">{idx + 1}</td>
                    <td className="py-1.5 px-1 text-text-primary font-medium truncate max-w-[140px]">
                      {team.team_name}
                    </td>
                    <td className="py-1.5 px-1 text-center text-text-secondary">{team.seed}</td>
                    {ROUND_COLS.map(col => (
                      <td key={col.key} className="py-1.5 px-1 text-right">
                        <PctCell value={team[col.key]} />
                      </td>
                    ))}
                    <td className="py-1.5 px-1 text-right text-text-secondary">
                      {team.avg_games_won.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Empty state */}
      {!simData && !loading && !error && (
        <div className="text-center py-8">
          <svg className="w-12 h-12 mx-auto text-text-tertiary/30 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
          </svg>
          <p className="text-sm text-text-tertiary">Select a strategy and run a simulation to see probabilities</p>
        </div>
      )}
    </div>
  );
}


function PctCell({ value }) {
  if (!value || value === 0) {
    return <span className="text-text-tertiary/30">-</span>;
  }

  // Color gradient: low=dim, high=bright
  let color = 'text-text-tertiary';
  if (value >= 75) color = 'text-green-400 font-semibold';
  else if (value >= 50) color = 'text-green-400/80';
  else if (value >= 25) color = 'text-accent-blue/80';
  else if (value >= 10) color = 'text-text-secondary';

  return <span className={color}>{value.toFixed(1)}%</span>;
}
